import { useState } from 'react';

import { IconTrash } from '@tabler/icons-react';
import { QuizDefault } from 'icons';
import { useTranslation } from 'react-i18next';

import { Quiz } from '@cfa/pathway-fe-shared/xp';

import useAuth from '@/hooks/useAuth';

import { PathwayCard } from '@/components/pathway-card';

import { routes } from 'constants/index';
import { mapCategoryToColor } from 'utils/categoryUtils';
import { itemByLanguage } from 'utils/language';

import DeleteQuizPopup from '../BuildQuizzesItems/DeleteQuizPopup';

interface QuizCardProps {
  quizDetails: Quiz;
  onDeleteQuiz: (id: string, name: string) => void;
}

const QuizCard: React.FC<QuizCardProps> = ({ quizDetails, onDeleteQuiz }) => {
  const { t } = useTranslation();
  const [showDeleteQuizPopup, setShowDeleteQuizPopup] = useState(false);
  const { data } = useAuth();
  const canManageThisQuiz =
    data?.locationsWithRoles.some(
      location =>
        location.permissions.canManageQuizzes &&
        location.ownerId === quizDetails.ownerId,
    ) ?? false;

  const quizName = itemByLanguage(quizDetails.name, '');

  const onDeleteQuizAndClosePopup = () => {
    if (quizDetails.id) {
      onDeleteQuiz(quizDetails.id, quizName);
    }
    setShowDeleteQuizPopup(false);
  };

  return (
    <>
      <PathwayCard.Root
        className="quizCard animate-[fadeIn_linear_0.3s] p-0!"
        data-testid="MyQuiz"
        link={routes.plans.managedQuiz({ quizId: quizDetails.id })}
      >
        <PathwayCard.Media
          className="items-center px-4 py-6"
          style={{ background: mapCategoryToColor('default') }}
        >
          <QuizDefault />
        </PathwayCard.Media>
        <PathwayCard.Body className="py-4">
          <PathwayCard.Title
            data-testid="QuizName"
            className="font-normal!"
            title={quizName}
          >
            {quizName}
          </PathwayCard.Title>
        </PathwayCard.Body>
        {canManageThisQuiz && (
          <PathwayCard.Actions className="pr-2">
            <button
              type="button"
              aria-label={t('TrainingPlans.manageThreeDotMenu.deleteQuiz')}
              data-testid="ThreeDotMenuDeleteQuizPopup"
              onClick={() => setShowDeleteQuizPopup(true)}
              className="text-status-error hover:bg-status-error/10 flex size-10 cursor-pointer items-center justify-center rounded-full"
            >
              <IconTrash data-testid="DeleteQuiz" size={24} />
            </button>
          </PathwayCard.Actions>
        )}
      </PathwayCard.Root>
      <DeleteQuizPopup
        onDeleteQuizAndClosePopup={onDeleteQuizAndClosePopup}
        quizName={quizName}
        setShowDeleteQuizPopup={setShowDeleteQuizPopup}
        showDeleteQuizPopup={showDeleteQuizPopup}
      />
    </>
  );
};

export default QuizCard;
